import type { BossState, GameState } from "@mmotdg/shared";
import * as THREE from "three";

// Procedural icosahedron boss with 4 phase-based degradation states and
// glowing chains toward each lane's base.
export class BossRenderer {
  private readonly group = new THREE.Group();
  private readonly coreGroup = new THREE.Group();
  private mesh: THREE.Mesh | null = null;
  private wireframe: THREE.LineSegments | null = null;
  private core: THREE.Mesh | null = null;
  private coreLight: THREE.PointLight | null = null;
  private chains: THREE.Line[] = [];
  private currentPhase = -1;
  private currentEffectAura: THREE.Mesh | null = null;
  private hover = 0;
  private readonly ownedGeometries: THREE.BufferGeometry[] = [];
  private readonly ownedMaterials: THREE.Material[] = [];
  private hubAnchor = new THREE.Vector3(0, 0, 0);
  private floatHeight = 6.5;

  constructor(private readonly scene: THREE.Scene) {
    this.group.add(this.coreGroup);
    this.scene.add(this.group);
  }

  // Positions the boss centered above the play field. The hub sits at the
  // map center, the icosahedron hovers above it.
  attachToState(state: GameState): void {
    this.hubAnchor.set(state.gridWidth / 2, 0, state.gridHeight / 2);
    this.group.position.copy(this.hubAnchor).setY(this.floatHeight);
    this.rebuildChains(state);
  }

  // For radial layouts the hub is at world origin and lane groups orbit it.
  // Caller provides the actual world XZ of each lane's spawn cell so chains
  // land exactly where mobs come out — independent of slice geometry.
  attachToHub(spawnWorldPositions: Array<{ x: number; z: number }>): void {
    this.hubAnchor.set(0, 0, 0);
    this.group.position.set(0, this.floatHeight, 0);
    this.rebuildChainsToSpawns(spawnWorldPositions);
  }

  private rebuildChainsToSpawns(targets: Array<{ x: number; z: number }>): void {
    for (const chain of this.chains) {
      this.group.remove(chain);
      chain.geometry.dispose();
      (chain.material as THREE.Material).dispose();
    }
    this.chains = [];

    for (const t of targets) {
      const target = new THREE.Vector3(t.x, -this.floatHeight + 0.5, t.z);
      const pts: THREE.Vector3[] = [];
      const segs = 12;
      for (let s = 0; s <= segs; s++) {
        const u = s / segs;
        const sag = -Math.sin(u * Math.PI) * 0.8;
        pts.push(
          new THREE.Vector3(
            target.x * u,
            target.y * u + sag,
            target.z * u,
          ),
        );
      }
      const geo = new THREE.BufferGeometry().setFromPoints(pts);
      const mat = new THREE.LineDashedMaterial({
        color: 0xff8ace,
        dashSize: 0.3,
        gapSize: 0.18,
        transparent: true,
        opacity: 0.7,
      });
      const line = new THREE.Line(geo, mat);
      line.computeLineDistances();
      this.group.add(line);
      this.chains.push(line);
    }
  }

  setState(boss: BossState | null, state: GameState): void {
    if (!boss) {
      this.group.visible = false;
      return;
    }
    this.group.visible = true;
    if (boss.currentPhase !== this.currentPhase) {
      this.currentPhase = boss.currentPhase;
      this.rebuildMesh(boss.currentPhase);
    }
    if (this.chains.length !== state.lanes.length) {
      this.rebuildChains(state);
    }

    const hpRatio = boss.hpMax > 0 ? boss.hp / boss.hpMax : 0;
    if (this.coreLight) {
      this.coreLight.intensity = 1.5 + (1 - hpRatio) * 3.5;
    }
    if (this.core) {
      const mat = this.core.material as THREE.MeshBasicMaterial;
      mat.opacity = 0.5 + (1 - hpRatio) * 0.4;
    }
  }

  applyEffectAura(kind: string | null): void {
    if (this.currentEffectAura) {
      this.group.remove(this.currentEffectAura);
      this.disposeMesh(this.currentEffectAura);
      this.currentEffectAura = null;
    }
    if (!kind) return;

    const color = this.effectColor(kind);
    const geo = this.trackGeo(new THREE.SphereGeometry(2.8, 32, 32));
    const mat = this.trackMat(new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.12,
      side: THREE.BackSide,
      blending: THREE.AdditiveBlending,
    }));
    const aura = new THREE.Mesh(geo, mat);
    this.group.add(aura);
    this.currentEffectAura = aura;
  }

  tick(elapsedSec: number, dt: number): void {
    this.hover += dt;
    if (this.mesh) {
      this.mesh.position.y = Math.sin(this.hover * 1.2) * 0.25;
      this.mesh.rotation.y += dt * 0.35;
      this.mesh.rotation.x += dt * 0.12;
    }
    if (this.wireframe && this.mesh) {
      this.wireframe.position.copy(this.mesh.position);
      this.wireframe.rotation.copy(this.mesh.rotation);
    }
    if (this.core && this.mesh) {
      this.core.position.copy(this.mesh.position);
      const pulse = 1 + Math.sin(elapsedSec * 3.5) * 0.1;
      this.core.scale.setScalar(pulse);
    }
    if (this.coreLight && this.mesh) {
      this.coreLight.position.copy(this.mesh.position).add(this.group.position);
    }
    if (this.currentEffectAura && this.mesh) {
      this.currentEffectAura.position.copy(this.mesh.position);
      const s = 1 + Math.sin(elapsedSec * 2.5) * 0.05;
      this.currentEffectAura.scale.setScalar(s);
    }

    for (const chain of this.chains) {
      const mat = chain.material as THREE.LineDashedMaterial;
      // Sine wave of opacity to suggest energy pulsing through the chain
      mat.opacity = 0.4 + 0.35 * (0.5 + 0.5 * Math.sin(elapsedSec * 3.0));
    }
  }

  dispose(): void {
    this.scene.remove(this.group);
    for (const g of this.ownedGeometries) g.dispose();
    for (const m of this.ownedMaterials) m.dispose();
    this.ownedGeometries.length = 0;
    this.ownedMaterials.length = 0;
    this.mesh = null;
    this.wireframe = null;
    this.core = null;
    this.coreLight = null;
    this.chains = [];
    this.currentEffectAura = null;
  }

  // ----- internals -----

  private rebuildMesh(phase: number): void {
    if (this.mesh) {
      this.coreGroup.remove(this.mesh);
      this.mesh = null;
    }
    if (this.wireframe) {
      this.coreGroup.remove(this.wireframe);
      this.wireframe = null;
    }
    if (this.core) {
      this.coreGroup.remove(this.core);
      this.core = null;
    }
    if (this.coreLight) {
      this.group.remove(this.coreLight);
      this.coreLight = null;
    }

    const detail = phase === 0 ? 1 : phase === 1 ? 2 : phase === 2 ? 1 : 0;
    const radius = 1.6 + (3 - phase) * 0.1; // ever-so-slightly bigger as it cracks

    const geo = this.trackGeo(new THREE.IcosahedronGeometry(radius, detail));

    // For phases 2/3 we yank a few vertices outward to give it a fractured look.
    if (phase >= 2) {
      this.shatterGeometry(geo, phase);
    }

    const phaseColors = [0x6ee7ff, 0xff8aa3, 0xff567c, 0xff1542];
    const emissiveColors = [0x102837, 0x301e26, 0x6b1633, 0xa0102e];
    const color = phaseColors[phase] ?? phaseColors[0]!;
    const emissive = emissiveColors[phase] ?? emissiveColors[0]!;

    const mat = this.trackMat(new THREE.MeshStandardMaterial({
      color,
      emissive,
      metalness: 0.5,
      roughness: phase >= 2 ? 0.7 : 0.3,
      flatShading: true,
      transparent: phase === 3,
      opacity: phase === 3 ? 0.85 : 1,
    }));
    const mesh = new THREE.Mesh(geo, mat);
    mesh.castShadow = true;
    this.coreGroup.add(mesh);
    this.mesh = mesh;

    // Wireframe overlay accentuates the polyhedral look
    const edges = this.trackGeo(new THREE.EdgesGeometry(geo));
    const wfMat = this.trackMat(new THREE.LineBasicMaterial({
      color: phase >= 2 ? 0xffffff : 0x9adcff,
      transparent: true,
      opacity: 0.65,
    }));
    const wf = new THREE.LineSegments(edges, wfMat);
    this.coreGroup.add(wf);
    this.wireframe = wf;

    // Inner glowing core — gets brighter as phase advances
    const coreGeo = this.trackGeo(new THREE.SphereGeometry(0.65 + phase * 0.15, 24, 24));
    const coreMat = this.trackMat(new THREE.MeshBasicMaterial({
      color: phase >= 2 ? 0xff3060 : 0x9ce7ff,
      transparent: true,
      opacity: 0.55,
      blending: THREE.AdditiveBlending,
    }));
    const core = new THREE.Mesh(coreGeo, coreMat);
    this.coreGroup.add(core);
    this.core = core;

    const light = new THREE.PointLight(phase >= 2 ? 0xff3060 : 0x9ce7ff, 1.5, 18, 1.4);
    this.group.add(light);
    this.coreLight = light;
  }

  private shatterGeometry(geo: THREE.BufferGeometry, phase: number): void {
    const pos = geo.getAttribute("position");
    if (!pos) return;
    const arr = pos.array as Float32Array;
    const skewAmount = phase === 3 ? 0.45 : 0.22;
    // Pseudo-deterministic noise so it doesn't reshuffle every render
    let seed = 42 + phase * 7;
    const rng = () => {
      seed = (seed * 1664525 + 1013904223) >>> 0;
      return ((seed / 0xffffffff) - 0.5) * 2;
    };
    for (let i = 0; i < arr.length; i += 3) {
      const dx = rng() * skewAmount;
      const dy = rng() * skewAmount;
      const dz = rng() * skewAmount;
      arr[i]! += dx;
      arr[i + 1]! += dy;
      arr[i + 2]! += dz;
    }
    pos.needsUpdate = true;
    geo.computeVertexNormals();
  }

  private rebuildChains(state: GameState): void {
    for (const chain of this.chains) {
      this.group.remove(chain);
      chain.geometry.dispose();
      (chain.material as THREE.Material).dispose();
    }
    this.chains = [];

    for (const lane of state.lanes) {
      const targetWorld = new THREE.Vector3(
        lane.baseCell.cx + 0.5 - this.hubAnchor.x,
        -this.floatHeight + 0.5,
        lane.baseCell.cy + 0.5 - this.hubAnchor.z,
      );
      const pts: THREE.Vector3[] = [];
      const segments = 12;
      for (let s = 0; s <= segments; s++) {
        const t = s / segments;
        const sag = -Math.sin(t * Math.PI) * 0.9;
        pts.push(
          new THREE.Vector3(
            targetWorld.x * t,
            (targetWorld.y * t) + sag,
            targetWorld.z * t,
          ),
        );
      }
      const geo = new THREE.BufferGeometry().setFromPoints(pts);
      const mat = new THREE.LineDashedMaterial({
        color: 0xff8ace,
        dashSize: 0.3,
        gapSize: 0.18,
        transparent: true,
        opacity: 0.7,
      });
      const line = new THREE.Line(geo, mat);
      line.computeLineDistances();
      this.group.add(line);
      this.chains.push(line);
    }
  }

  private effectColor(kind: string): number {
    // Mob-family = orange, tower-family = blue, economy-family = gold
    const mobs = new Set(["surge", "bloodlust", "reinforcement", "echo"]);
    const towers = new Set(["frostbite", "static_field", "curse", "shadow_veil"]);
    if (mobs.has(kind)) return 0xff8a56;
    if (towers.has(kind)) return 0x6ec4ff;
    return 0xffd700;
  }

  private trackGeo<T extends THREE.BufferGeometry>(geo: T): T {
    this.ownedGeometries.push(geo);
    return geo;
  }

  private trackMat<T extends THREE.Material>(mat: T): T {
    this.ownedMaterials.push(mat);
    return mat;
  }

  private disposeMesh(mesh: THREE.Mesh): void {
    mesh.geometry.dispose();
    if (Array.isArray(mesh.material)) {
      for (const m of mesh.material) m.dispose();
    } else {
      mesh.material.dispose();
    }
  }
}
